import {ErrorBoundary} from '@sentry/react';
import {QueryErrorResetBoundary} from '@tanstack/react-query';
import React, {Suspense, useEffect, useReducer, useState} from 'react';
import {doNothing} from '~/library/toolbox/src';
import {SkeletonContextProvider} from '~/library/ui-kit';
import {LoadProblemPane, SkeletonPageLayout} from './SkeletonPageLayout';

export interface FallbackRenderProps {
    reset: () => void;
    threw: boolean;
    error?: unknown;
    title?: string;
    skeleton?: React.JSX.Element;
    children?: React.JSX.Element;
    resetTs?: number;
}

export interface FallbackComponent {
    (props: FallbackRenderProps): React.JSX.Element;
}

interface FullFallbackProps {
    Fallback: FallbackComponent;
    skeleton?: undefined;
}

interface DefaultFallbackProps {
    skeleton: React.JSX.Element;
    Fallback?: undefined;
}

export function SuspenseAndErrorBoundary({
    children,
    Fallback = DeprecatedDefaultFallback,
    skeleton,
}: React.PropsWithChildren<FullFallbackProps | DefaultFallbackProps> & {
    title?: string;
}) {
    const [resets, dispatchReset] = useReducer(
        (state: {ts: number; count: number}, resetTime: number) => ({
            ts: resetTime,
            count: state.count + 1,
        }),
        {ts: 0, count: 0}
    );
    return (
        <QueryErrorResetBoundary>
            {({reset: resetQueries}) => (
                <ErrorBoundary
                    fallback={({resetError, error}) => (
                        <Fallback threw reset={resetError} error={error} skeleton={skeleton} resetTs={resets.ts} />
                    )}
                    onReset={() => {
                        if (resets.count >= 3) {
                            window.location.reload();
                            return;
                        }
                        dispatchReset(Date.now());
                        resetQueries();
                    }}
                >
                    <Suspense fallback={<Fallback threw={false} reset={doNothing} skeleton={skeleton} />}>
                        {children}
                    </Suspense>
                </ErrorBoundary>
            )}
        </QueryErrorResetBoundary>
    );
}

/**
 * @deprecated
 */
function DeprecatedDefaultFallback({threw, reset, skeleton, title, resetTs = 0}: FallbackRenderProps) {
    threw = useDelayOnThrewFlag(threw, resetTs);

    return (
        <SkeletonPageLayout title={title} stopped={threw} reset={reset}>
            {skeleton}
        </SkeletonPageLayout>
    );
}

export function BoundaryFallbackDefault({threw, reset, skeleton, children, resetTs = 0}: FallbackRenderProps) {
    threw = useDelayOnThrewFlag(threw, resetTs);

    return (
        <SkeletonContextProvider value={{stopped: threw}}>
            {skeleton || children}
            {threw && <LoadProblemPane reset={reset} />}
        </SkeletonContextProvider>
    );
}

function useDelayOnThrewFlag(threw: boolean, resetTs: number) {
    const [crashTs, setCrashTs] = useState(() => (threw ? Date.now() : 0));
    const threwRecently = threw && crashTs - resetTs < 1000;

    useEffect(() => {
        if (threwRecently) {
            const timeoutId = setTimeout(() => {
                setCrashTs(Date.now());
            }, 1000);
            return () => {
                clearTimeout(timeoutId);
            };
        }
    }, [threwRecently]);

    return threwRecently ? false : threw;
}
